import React, { useMemo, useState } from "react";
import Footer from "components/commons/footer/Footer";
import CardWrapper from "components/commons/cardWrapper/CardWrapper";
import { useAppSelector } from "utils/store";
import { useRequest } from "../../../../../hooks/useRequest";
import {
  createCampaignThunk,
  submitAdOrderThunk,
} from "../../../../../core/orderAd/orderAd.thunk";
import styles from "./Preview.module.scss";
import footerStyles from "../../../orderAd/orderDetails/OrderDetails.module.scss";
import classNames from "classnames";
import { ApiAdRecord } from "../../../../../core/orderAd/orderAd.entity";
import { digitsToHindi, toMoneyFormat } from "@bale/utils";
import { AccountType } from "../../../../../core/auth/auth.entity";
import { strings } from "../../../../../intl/fa";
import { NewButton, NewButtonSize } from "@bale/ui";
import { useMyPanel } from "../../../../../hooks/useMyPanel";
import { AdType } from "../../../../../core/orderAd/orderAd.slice";
import Modal from "components/commons/modal/Modal";
import { Bubble } from "./Bubble";
import * as Sentry from "@sentry/react";
import { useCalculatedPrice } from "hooks/useCalculatedPrice";
import { useCampaignPrice } from "hooks/useCampaignPrice";

type FooterProps = {
  adRecord: ApiAdRecord & { adType?: AdType };
  isCampaign?: boolean;
};
const PriceFooter: React.FC<React.PropsWithChildren<FooterProps>> = (props) => {
  const myPanel = useMyPanel();
  const loading = useAppSelector((state) => state.orderAd.orderLoading);

  const { sendRequest: submitAdOrder } = useRequest(submitAdOrderThunk);
  const { sendRequest: createCampaign } = useRequest(createCampaignThunk);

  function traceOrdersInSentry() {
    Sentry.captureException({
      message: `Ordering ${props.isCampaign ? "cmapaign" : "ad"}`,
      extra: { adData: JSON.stringify(props.adRecord) },
    });
  }

  const submit = () => {
    traceOrdersInSentry();
    if (!loading) {
      if (props.isCampaign) {
        createCampaign();
      } else {
        submitAdOrder();
      }
    }
  };
  const { finalPrice, totalPrice, finalBasePrice } = useCalculatedPrice({
    link: props.adRecord.link,
    viewCount: props.adRecord.set_view,
    spot: props.adRecord.spot,
    clickCount: props.adRecord.set_click,
  });

  const campaignPrice = useCampaignPrice({
    description: props.adRecord.link ?? props.adRecord.description,
    adSpot: props.adRecord.spot,
    adType: props.adRecord.adType,
  });

  const adBasePrice = useMemo<number>(() => {
    if (props.adRecord.view_count > 0) {
      return finalBasePrice / props.adRecord.view_count;
    } else if (props.adRecord.click_count > 0) {
      return finalBasePrice / props.adRecord.click_count;
    }
    return 1;
  }, [props.adRecord.view_count, props.adRecord.click_count, finalBasePrice]);

  const isDiscountActive = totalPrice !== finalPrice;
  const totalPriceClasses = `${footerStyles.PriceAmount} ${
    isDiscountActive ? footerStyles.strikethrough : ""
  }`;
  const finalPriceClasses = `${footerStyles.PriceAmount} ${footerStyles.DiscountedPrice}`;
  const showPrice = myPanel.type === AccountType.Personal;
  const basePriceText = (
    <div className={styles.FlexSpaceBetween}>
      <div>
        <span>{strings.common.view + strings.preview.each_person + " "}</span>
        <span className={totalPriceClasses}>
          {digitsToHindi(
            toMoneyFormat(
              String(props.isCampaign ? campaignPrice : finalBasePrice)
            )
          ) +
            " " +
            strings.common.rial}
        </span>
        {isDiscountActive && !props.isCampaign && (
          <span className={finalPriceClasses}>
            {digitsToHindi(toMoneyFormat(String(finalBasePrice))) +
              " " +
              strings.common.rial}
          </span>
        )}
      </div>
      {isDiscountActive && (
        <div className={styles.ChannelAid}>
          {strings.ad_sale.small_channels_aid}
        </div>
      )}
    </div>
  );

  return (
    <Footer className={footerStyles.Footer}>
      <div className={footerStyles.FooterContent}>
        {showPrice && (
          <>
            {basePriceText}
            {!props.isCampaign && (
              <>
                <div className={footerStyles.PriceIndicator}>
                  <p className={footerStyles.PriceLabel}>
                    {strings.order_details.final_price}
                  </p>

                  <p className={finalPriceClasses}>
                    {digitsToHindi(
                      toMoneyFormat(
                        String(finalBasePrice * props.adRecord.view_count)
                      ) +
                        " " +
                        strings.common.rial
                    )}
                  </p>
                  {isDiscountActive && (
                    <p className={totalPriceClasses}>
                      {digitsToHindi(
                        toMoneyFormat(
                          String(adBasePrice * props.adRecord.view_count)
                        ) +
                          " " +
                          strings.common.rial
                      )}
                    </p>
                  )}
                </div>
              </>
            )}
          </>
        )}
        <NewButton
          text={strings.final_submit}
          size={NewButtonSize.LARGE}
          onClick={submit}
          loading={loading}
        />
      </div>
    </Footer>
  );
};

type RowProps = {
  rowKey?: string;
  value?: string;
  ltr?: boolean;
};
const Row: React.FC<React.PropsWithChildren<RowProps>> = (props) => {
  return (
    <div className={styles.Row}>
      <p className={styles.Key}>{props.rowKey}</p>
      <p
        className={styles.Value}
        style={{ direction: props.ltr ? "ltr" : "rtl" }}
      >
        {props.value}
      </p>
    </div>
  );
};

interface Props {
  isCampaign?: boolean;
}
const Preview: React.FC<React.PropsWithChildren<Props>> = ({ isCampaign }) => {
  const [showPreviewModal, setshowPreviewModal] = useState(false);
  const orderAdInfo = useAppSelector((state) => state.orderAd.info);

  const previewClickHandler = () => {
    setshowPreviewModal(true);
  };

  return (
    <>
      <CardWrapper className={classNames(styles.Card, styles.PreviewCard)}>
        <div className={styles.TitleWithButton}>
          <p className={styles.Title}>
            {strings.sponsored_messages.preview.order_info}
          </p>
          <p className={styles.PreviewLink} onClick={previewClickHandler}>
            {strings.sponsored_messages.preview.order_preview}
          </p>
        </div>
        <div className={styles.RowsWrapper}>
          <Row
            rowKey={strings.sponsored_messages.preview.ad_description}
            value={orderAdInfo.description}
          />
          <Row
            rowKey={strings.sponsored_messages.preview.view_count}
            value={digitsToHindi(toMoneyFormat(String(orderAdInfo.view_count)))}
          />
        </div>
      </CardWrapper>
      <PriceFooter
        adRecord={orderAdInfo as ApiAdRecord}
        isCampaign={isCampaign}
      />
      {showPreviewModal && (
        <Modal
          title={strings.sponsored_messages.preview.order_preview}
          mobileBottomSheet
          primaryButton={{
            onClick: () => setshowPreviewModal(false),
            label: strings.common.button.close,
          }}
          onClose={() => setshowPreviewModal(false)}
        >
          <p className={styles.ModalHelper}>
            {strings.sponsored_messages.preview.preview_modal_helper}
          </p>
          {orderAdInfo.description && (
            <Bubble
              caption={orderAdInfo.description}
              imageSrc={orderAdInfo.croppedImageURL}
            />
          )}
        </Modal>
      )}
    </>
  );
};

export default Preview;
